import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, DollarSign, Video, ShoppingBag, BookOpen, Check, X, Upload, TrendingUp, Crown, Lock, Unlock, Settings, RefreshCw, Globe, Edit3, FileSpreadsheet, AlertCircle, Download, Youtube, Plus, Layers, Calendar, Flag, Star, Trash2, Gift, Ban, UserX, UserCheck, CheckCircle, ShieldCheck, Clock, PartyPopper, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Featured Members tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure presentational — receives parent state + handlers via props.
 */
export default function FeaturedMembersTab(props) {
  const { user, navigate, getAuthHeader, fetchAllData, fileInputRef, users, setUsers, userSearch, setUserSearch, filteredUsers, pendingFounders, founderSettings, setFounderSettings, founderSeatsRemaining, updateFounderSettings, toggleFounderEnrollment, toggleFoundingMember, suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier, sales, pendingSales, pendingListingSales, handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale, handleCancelListingSale, commissionDashboard, promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode, promoToDelete, setPromoToDelete, potData, setPotData, updatePot, advertisers, pendingVideos, pendingLibraryVideos, handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview, moveDownlineData, setMoveDownlineData, featuredMembers, selectedUserToFeature, setSelectedUserToFeature, featuredText, setFeaturedText, handleAddFeaturedMember, handleRemoveFeaturedMember, tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures, startEditingTier, saveTierFeatures, customTiers, showTierForm, setShowTierForm, newTier, setNewTier, tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier, pendingServices, flaggedServices, handleApproveService, handleFlagService, handleDeleteService, edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions, importing, importResult, handleFileSelect, downloadSampleCSV } = props;

  return (
    <div className="space-y-4" data-testid="featured-tab">
      <h2 className="text-2xl font-playfair font-bold text-foreground mb-4 flex items-center gap-2">
                    <Star className="w-6 h-6 text-yellow-500" /> Featured Members
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Select members to feature on the logged-in homepage. Featured members will be displayed prominently to all platform users.
                  </p>

                  {/* Add Featured Member Form */}
                  <div className="wm-card p-6 rounded-lg border border-border mb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Add Featured Member</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-foreground">Select Member</Label>
                        <select
                          value={selectedUserToFeature}
                          onChange={(e) => setSelectedUserToFeature(e.target.value)}
                          className="w-full h-10 px-3 bg-input border border-border rounded-md text-foreground mt-1"
                          data-testid="select-featured-user"
                        >
                          <option value="">Select a member...</option>
                          {users
                            .filter(u => !u.is_admin && !featuredMembers.some(fm => fm.user_id === u.id))
                            .map(u => (
                              <option key={u.id} value={u.id}>
                                {u.name} ({u.membership_tier})
                              </option>
                            ))
                          }
                        </select>
                      </div>
                      <div>
                        <Label className="text-foreground">Featured Text (optional)</Label>
                        <Input
                          value={featuredText}
                          onChange={(e) => setFeaturedText(e.target.value)}
                          placeholder="Custom callout text..."
                          className="bg-input border-border mt-1"
                          data-testid="featured-text-input"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={handleAddFeaturedMember}
                          className="bg-gold-gradient text-black hover:shadow-gold-glow font-bold"
                          data-testid="add-featured-btn"
                        >
                          <Plus className="mr-2" size={18} /> Feature Member
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Current Featured Members */}
                  <div className="wm-card p-6 rounded-lg border border-border">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Currently Featured ({featuredMembers.length})
                    </h3>
              
                    {featuredMembers.length > 0 ? (
                      <div className="space-y-3">
                        {featuredMembers.map((fm, idx) => (
                          <div 
                            key={fm.id}
                            className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                            data-testid={`featured-member-${fm.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <span className="text-gold font-bold">#{idx + 1}</span>
                              <div>
                                <p className="font-medium text-foreground">{fm.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {fm.membership_tier} • {fm.specialty || 'No specialty set'}
                                </p>
                                {fm.featured_text && (
                                  <p className="text-sm text-gold mt-1">"{fm.featured_text}"</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Link to={`/member/${fm.user_id}`} target="_blank">
                                <Button variant="outline" size="sm" className="border-border">
                                  View Profile
                                </Button>
                              </Link>
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                onClick={() => handleRemoveFeaturedMember(fm.id)}
                                data-testid={`remove-featured-${fm.id}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        No featured members yet. Add members above to feature them on the homepage.
                      </p>
                    )}
                  </div>
    </div>
  );
}
